import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { isPlannerOrAbove } from '@peles/shared';
import { useAuth } from '../../context/AuthContext';
import { useGlobalShortcuts } from '../../hooks/useGlobalShortcuts';
import { CommandPalette } from './CommandPalette';
import { ShortcutsHelp } from './ShortcutsHelp';

interface CommandCenterValue {
  /** Whether quick search and the shortcuts are available to this person. */
  available: boolean;
  openSearch: () => void;
  openHelp: () => void;
}

const CommandCenterContext = createContext<CommandCenterValue>({
  available: false,
  openSearch: () => {},
  openHelp: () => {},
});

export const useCommandCenter = () => useContext(CommandCenterContext);

/**
 * Owns quick search, the shortcuts help and the keys that open them, for the
 * signed-in app. Producers only: search reads records a client may not see,
 * and the pages the shortcuts go to are producer pages.
 */
export function CommandCenter({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const available = !!user && isPlannerOrAbove(user.systemRole);
  const [open, setOpen] = useState<'search' | 'help' | null>(null);
  const openSearch = useCallback(() => setOpen('search'), []);
  const openHelp = useCallback(() => setOpen('help'), []);
  const close = useCallback(() => setOpen(null), []);

  useGlobalShortcuts(user?.systemRole, { openSearch, openHelp }, available);

  const value = useMemo(
    () => ({ available, openSearch, openHelp }),
    [available, openSearch, openHelp],
  );

  return (
    <CommandCenterContext.Provider value={value}>
      {children}
      {available && (
        <>
          <CommandPalette open={open === 'search'} onClose={close} role={user?.systemRole} />
          <ShortcutsHelp open={open === 'help'} onClose={close} role={user?.systemRole} />
        </>
      )}
    </CommandCenterContext.Provider>
  );
}
